'use client';

import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';

type HeaderNewProps = {
  currentLang: string;
  initialData?: any;
  dict?: any;
};

const HeaderNew = ({ currentLang, initialData }: HeaderNewProps) => {
  console.log('🔍 [HeaderNew] Received initialData:', initialData);

  // Auth context
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const isAuthenticated = !!user;

  // State management
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isWebsiteDropdownOpen, setIsWebsiteDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const [activeMobileAccordion, setActiveMobileAccordion] = useState<string | null>('collapseOne');
  const [headerData, setHeaderData] = useState(initialData);
  const [megaNavData, setMegaNavData] = useState<any>(null);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);

  // Extract mega nav data from initialData
  useEffect(() => {
    if (initialData?.rawData?.specifications) {
      const findServicesSpec = initialData.rawData.specifications.find((s: any) => s.key === 'find-services');
      const learnSpec = initialData.rawData.specifications.find((s: any) => s.key === 'learn');

      console.log('🔍 [HeaderNew] Find Services spec:', findServicesSpec);
      console.log('🔍 [HeaderNew] Learn spec:', learnSpec);

      setMegaNavData({
        findServices: findServicesSpec?.value,
        learn: learnSpec?.value
      });
    }
  }, [initialData]);

  // Helper to get value from nested spec array
  const getSpecValue = (data: any[], key: string) => {
    if (!Array.isArray(data)) return null;
    const item = data.find((d: any) => d.key === key);
    return item?.value;
  };

  // Helper to slugify text for URLs
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to get link or generate default
  const getLinkOrDefault = (link: string | null | undefined, label: string) => {
    if (link && link !== '' && link !== '/') {
      return link;
    }
    return `/${currentLang}/page/${slugify(label)}`;
  };

  // Get Learn data
  const learnSection1 = megaNavData?.learn ? getSpecValue(megaNavData.learn, 'section-1') : null;
  const learnSubSections = megaNavData?.learn ? getSpecValue(megaNavData.learn, 'sub-sections') : null;
  const learnIcon = megaNavData?.learn ? getSpecValue(megaNavData.learn, 'icon') : null;

  // Extract Learn section-1 values
  const learnName = learnSection1 ? getSpecValue(learnSection1, 'name') : 'Learn';
  const learnDescription = learnSection1 ? getSpecValue(learnSection1, 'description') : '';
  const learnButtonText = learnSection1 ? getSpecValue(learnSection1, 'button-text') : 'Get More Resources';
  const learnButtonLink = learnSection1 ? getSpecValue(learnSection1, 'button-link') : '/';

  // Get Find Services data
  const findServicesSection1 = megaNavData?.findServices ? getSpecValue(megaNavData.findServices, 'section-1') : null;
  const findServicesSection2 = megaNavData?.findServices ? getSpecValue(megaNavData.findServices, 'section-2') : null;
  const findServicesIcon = megaNavData?.findServices ? getSpecValue(megaNavData.findServices, 'icon') : null;

  // Extract Find Services section-1 values
  const findServicesName = findServicesSection1 ? getSpecValue(findServicesSection1, 'name') : 'Find Services';
  const findServicesDescription = findServicesSection1 ? getSpecValue(findServicesSection1, 'description') : '';
  const findServicesButtonText = findServicesSection1 ? getSpecValue(findServicesSection1, 'button-text') : 'Browse Services';
  const findServicesButtonLink = findServicesSection1 ? getSpecValue(findServicesSection1, 'button-link') : '/find-services';

  // Extract Find Services section-2 values
  const findServicesSection2Name = findServicesSection2 ? getSpecValue(findServicesSection2, 'name') : 'Popular Service Topics';
  const findServicesLinks = findServicesSection2 ? getSpecValue(findServicesSection2, 'links') : [];

  console.log('🔍 [HeaderNew] Learn sub-sections:', learnSubSections);
  console.log('🔍 [HeaderNew] Find Services links:', findServicesLinks);

  // Sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  // Toggle search
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Toggle website dropdown
  const toggleWebsiteDropdown = () => {
    setIsWebsiteDropdownOpen(!isWebsiteDropdownOpen);
  };

  // Toggle mobile accordion
  const toggleMobileAccordion = (id: string) => {
    setActiveMobileAccordion(activeMobileAccordion === id ? null : id);
  };

  // Toggle mega menu
  const toggleMegaMenu = (menuName: string) => {
    setActiveMegaMenu(activeMegaMenu === menuName ? null : menuName);
  };

  // Hide menus when a nav link is clicked (mobile and desktop)
  const handleNavLinkClick = () => {
    setIsMobileMenuOpen(false);
    setActiveMobileAccordion(null);
    setIsSearchOpen(false);
    setIsWebsiteDropdownOpen(false);
    setActiveMegaMenu(null);
    try {
      if (typeof document !== 'undefined') {
        document.body.setAttribute('data-hide-mega', 'true');
      }
    } catch { }
  };

  // Ensure the force-hide attribute isn't left around on re-mounts
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.removeAttribute('data-hide-mega');
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.removeAttribute('data-hide-mega');
      }
    };
  }, []);

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside mega menu and not on the menu trigger
      if (activeMegaMenu) {
        const isInsideMegaMenu = target.closest('.mega-menu');
        const isOnMenuTrigger = target.closest('#menu > ul > li > a');
        if (!isInsideMegaMenu && !isOnMenuTrigger) {
          setActiveMegaMenu(null);
        }
      }
    };

    if (activeMegaMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeMegaMenu]);

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Search:', searchQuery);
    // Implement search logic here
  };

  return (
    <header className={isSticky ? 'sticky' : ''}>
      <style jsx global>{`
        [data-hide-mega='true'] .mega-menu { display: none !important; }
      `}</style>
      {/* Mobile Menu */}
      <div className="d-block d-xl-none d-lg-none">
        <div
          className="menu_overlay"
          style={{ display: isMobileMenuOpen ? 'block' : 'none' }}
          onClick={toggleMobileMenu}
        ></div>

        <div className={`mobile_menu_wrap ${isMobileMenuOpen ? 'menu_show' : ''}`}>
          <div className="accordion" id="header-accord">
            {/* Find Services Accordion */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={`accordion-button ${activeMobileAccordion === 'collapseOne' ? '' : 'collapsed'}`}
                  type="button"
                  onClick={() => toggleMobileAccordion('collapseOne')}
                  aria-expanded={activeMobileAccordion === 'collapseOne'}
                  aria-controls="collapseOne"
                >
                  <img src={findServicesIcon || "/images/icons/service_icon.png"} alt="" />
                  Find Services
                </button>
              </h2>
              <div
                id="collapseOne"
                className={`accordion-collapse collapse ${activeMobileAccordion === 'collapseOne' ? 'show' : ''}`}
                aria-labelledby="headingOne"
              >
                <div className="accordion-body">
                  <div className="top-content">
                    <h4>{findServicesName}</h4>
                    <p>{findServicesDescription}</p>
                    <a href={findServicesButtonLink} className="link" onClick={handleNavLinkClick}>
                      <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                      {findServicesButtonText}
                    </a>
                  </div>
                  <hr />
                  <div className="menu-hub">
                    <h6>{findServicesSection2Name}</h6>
                    <ul>
                      {Array.isArray(findServicesLinks) && findServicesLinks.map((link: any, index: number) => (
                        <li key={index}>
                          <a href={getLinkOrDefault(link.link, link.label)} onClick={handleNavLinkClick}>{link.label}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Learn Accordion */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingTwo">
                <button
                  className={`accordion-button ${activeMobileAccordion === 'collapseTwo' ? '' : 'collapsed'}`}
                  type="button"
                  onClick={() => toggleMobileAccordion('collapseTwo')}
                  aria-expanded={activeMobileAccordion === 'collapseTwo'}
                  aria-controls="collapseTwo"
                >
                  <img src={learnIcon || "/images/icons/learn_icon.png"} alt="" />
                  Learn
                </button>
              </h2>
              <div
                id="collapseTwo"
                className={`accordion-collapse collapse ${activeMobileAccordion === 'collapseTwo' ? 'show' : ''}`}
                aria-labelledby="headingTwo"
              >
                <div className="accordion-body">
                  <div className="top-content">
                    <h4>{learnName}</h4>
                    <p>{learnDescription}</p>
                    <a href={learnButtonLink} className="link" onClick={handleNavLinkClick}>
                      <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                      {learnButtonText}
                    </a>
                  </div>
                  <hr />
                  <div className="menu-hub">
                    {/* Dynamic Learn sub-sections for mobile */}
                    {learnSubSections && Array.isArray(learnSubSections) && learnSubSections.map((section: any, sectionIndex: number) => (
                      <div key={sectionIndex} className="mb-4">
                        <h6>{section.name}</h6>
                        <ul>
                          {Array.isArray(section.links) && section.links.map((link: any, linkIndex: number) => (
                            <li key={linkIndex}>
                              <a href={getLinkOrDefault(link.link, link.label)} onClick={handleNavLinkClick}>{link.label}</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="action-link">
            <ul>
              {/* Dynamic top bar links for mobile */}
              {headerData?.topBar?.links?.map((link: any, index: number) => (
                <li key={index}>
                  <a href={link.href} onClick={handleNavLinkClick}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>
          <hr />
          <div className="web-info">
            <h6>Our Websites</h6>
            <div className="web-list">
              <ul>
                <li>
                  <a href="https://pointters.org/">
                    {/* <img src="/images/icons/web-1.png" alt="" /> */}
                    Pointters
                  </a>
                </li>

              </ul>
            </div>
          </div>
        </div>

        {/* Mobile Topbar */}
        <div className="topbar">
          <div className="top-gray">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  {isAuthenticated ? (
                    <a href="/en/dashboard">
                      <img src="/images/icons/login_icon.svg" alt="" />
                      Dashboard
                    </a>
                  ) : (
                    <>
                      <a href="/en/login">
                        <img src="/images/icons/login_icon.svg" alt="" />
                        Login
                      </a>
                      <a href="/en/register">
                        <img src="/images/icons/register_icon.svg" alt="" />
                        Register
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <div className="row">
              <div className="col-6 align-self-center">
                <div className="logo">
                  <a href={headerData?.logo?.href || `/${currentLang}`} onClick={handleNavLinkClick}>
                    <img src={headerData?.logo?.src || "/images/logos/logo.png"} alt="Logo" />
                  </a>
                </div>
              </div>
              <div className="col-6 d-flex justify-content-end align-items-center align-self-center">
                <div className="search">
                  <div className="search-box" style={{ display: isSearchOpen ? 'block' : 'none' }}>
                    <form onSubmit={handleSearchSubmit}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <button type="submit">
                        <img src="/images/icons/search_icon.svg" alt="" />
                      </button>
                    </form>
                  </div>
                  <a
                    href="javascript:;"
                    className="show-search"
                    onClick={toggleSearch}
                    style={{ display: isSearchOpen ? 'none' : 'block' }}
                  >
                    <img src="/images/icons/search_icon.svg" alt="" />
                  </a>
                  <a
                    href="javascript:;"
                    className="hide-search"
                    onClick={toggleSearch}
                    style={{ display: isSearchOpen ? 'block' : 'none' }}
                  >
                    <img src="/images/icons/cross.svg" alt="" />
                  </a>
                </div>
                <ul className="top_right_contents">
                  <li>
                    <button
                      className={`hamburger menu_open ${isMobileMenuOpen ? 'close-ham' : ''}`}
                      aria-label="Toggle menu"
                      aria-expanded={isMobileMenuOpen}
                      type="button"
                      onClick={toggleMobileMenu}
                    >
                      <span className="bar bar1"></span>
                      <span className="bar bar2"></span>
                      <span className="bar bar3"></span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Menu */}
      <div className="d-none d-xl-block d-lg-block">
        <div className="menubar">
          {/* Desktop Topbar */}
          <div className="topbar">
            <div className="container">
              <div className="row">
                <div className="col-lg-12 d-flex justify-content-end">
                  <ul>
                    {/* Dynamic top bar links */}
                    {headerData?.topBar?.links?.map((link: any, index: number) => (
                      <li key={index}>
                        <a href={link.href}>{link.label}</a>
                      </li>
                    ))}

                    {/* Auth links */}
                    {isAuthenticated ? (
                      <li>
                        <a href={`/${currentLang}/dashboard`}>
                          <img src="/images/icons/login_icon.svg" alt="" />
                          Dashboard
                        </a>
                      </li>
                    ) : (
                      <>
                        <li>
                          <a href={`/${currentLang}/login`}>
                            <img src="/images/icons/login_icon.svg" alt="" />
                            Login
                          </a>
                        </li>
                        <li>
                          <a href={`/${currentLang}/register`}>
                            <img src="/images/icons/register_icon.svg" alt="" />
                            Register
                          </a>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Bottom Bar */}
          <div className="botom-bar">
            <div className="container">
              <div className="row justify-content-between align-items-center">
                <div className="col-lg-2 align-self-center">
                  <div className="logo">
                    <a href={headerData?.logo?.href || `/${currentLang}`}>
                      <img src={headerData?.logo?.src || "/images/logos/logo.png"} alt="Logo" />
                    </a>
                  </div>
                </div>
                <div className="col-lg-10 align-self-center d-flex justify-content-end align-items-center">
                  <div id="menu">
                    <ul>
                      {/* Find Services with Mega Menu */}
                      <li>
                        <img src={findServicesIcon || "/images/icons/service_icon.png"} alt="" />
                        <a
                          href="#"
                          data-no-loader="true"
                          suppressHydrationWarning
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMegaMenu('services');
                          }}
                        >
                          Find Services
                          <img src="/images/icons/drop_icon.png" alt="" />
                        </a>
                        {activeMegaMenu === 'services' && (
                          <div className="mega-menu">
                            <div className="main-box">
                              <div className="container">
                                <div className="row">
                                  <div className="col-lg-3">
                                    <div className="top-description">
                                      <h5>{findServicesName}</h5>
                                      <p>{findServicesDescription}</p>
                                      <a href={findServicesButtonLink} className="btn btn-secondary" onClick={handleNavLinkClick}>
                                        <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                                        {findServicesButtonText}
                                      </a>
                                    </div>
                                  </div>

                                  {/* Dynamic Find Services links grouped by group property */}
                                  {Array.isArray(findServicesLinks) && ['1', '2', '3'].map((groupNum) => {
                                    const groupLinks = findServicesLinks.filter((link: any) => link.group === groupNum);
                                    return (
                                      <div key={groupNum} className="col-lg-3">
                                        {groupNum === '1' && <h5>{findServicesSection2Name}</h5>}
                                        {groupNum !== '1' && <h5></h5>}
                                        <div className="link-box">
                                          <ul>
                                            {groupLinks.map((link: any, index: number) => (
                                              <li key={index}>
                                                <a href={getLinkOrDefault(link.link, link.label)} onClick={handleNavLinkClick}>{link.label}</a>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </li>

                      {/* Learn with Mega Menu */}
                      <li>
                        <img src={learnIcon || "/images/icons/learn_icon.png"} alt="" />
                        <a
                          href="/learn"
                        >
                          Learn
                          <img
                            src="/images/icons/drop_icon.png"
                            alt=""
                            data-no-loader="true"
                            suppressHydrationWarning
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMegaMenu('learn');
                            }}
                          />
                        </a>
                        {activeMegaMenu === 'learn' && (
                          <div className="mega-menu">
                            <div className="main-box learn-box">
                              <div className="container">
                                <div className="row top-box align-items-center">
                                  <div className="col-lg-3">
                                    <h3>{learnName}</h3>
                                  </div>
                                  <div className="col-lg-6">
                                    <p>{learnDescription}</p>
                                  </div>
                                  <div className="col-lg-3 d-flex justify-content-end">
                                    <a href={learnButtonLink} className="btn btn-secondary" onClick={handleNavLinkClick}>
                                      <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                                      {learnButtonText}
                                    </a>
                                  </div>
                                </div>
                                <div className="row sponserd-list">
                                  {/* Dynamic Learn sub-sections */}
                                  {learnSubSections && Array.isArray(learnSubSections) ? (
                                    learnSubSections.map((section: any, sectionIndex: number) => (
                                      <div key={sectionIndex} className="col-lg-3">
                                        <h5>{section.name}</h5>
                                        <div className="listbox">
                                          <ul>
                                            {Array.isArray(section.links) && section.links.map((link: any, linkIndex: number) => {
                                              const linkHref = getLinkOrDefault(link.link, link.label);
                                              return (
                                                <li key={linkIndex}>
                                                  <span><a href={linkHref} onClick={handleNavLinkClick}>{link.label}</a></span>
                                                  <div className="icon">
                                                    {link.image && (
                                                      <a href={linkHref} onClick={handleNavLinkClick}>
                                                        <img src={link.image} alt={link.label} />
                                                      </a>
                                                    )}
                                                  </div>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="col-12">
                                      <p>Loading resources...</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    </ul>
                  </div>

                  {/* Desktop Search */}
                  <div className="search">
                    <div className="search-box" style={{ display: isSearchOpen ? 'block' : 'none' }}>
                      <form onSubmit={handleSearchSubmit}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button type="submit">
                          <img src="/images/icons/search_icon.svg" alt="" />
                        </button>
                      </form>
                    </div>
                    <a
                      href="javascript:;"
                      className="show-search"
                      onClick={toggleSearch}
                      style={{ display: isSearchOpen ? 'none' : 'block' }}
                    >
                      <img src="/images/icons/search_icon.svg" alt="" />
                    </a>
                    <a
                      href="javascript:;"
                      className="hide-search"
                      onClick={toggleSearch}
                      style={{ display: isSearchOpen ? 'block' : 'none' }}
                    >
                      <img src="/images/icons/cross.svg" alt="" />
                    </a>
                  </div>

                  {/* Website Toggle */}
                  <div className={`web-toggle ${isWebsiteDropdownOpen ? 'active' : ''}`}>
                    <a href="javascript:;" className="btn btn-primary" onClick={toggleWebsiteDropdown}>
                      <img src="/images/icons/drop_down.svg" alt="" />
                      Our Website
                    </a>
                  </div>

                  {/* Website Dropdown List */}
                  <div className={`web-list ${isWebsiteDropdownOpen ? 'show' : ''}`}>
                    <ul>
                      <li>
                        <a href="https://pointters.org/">
                          <img width={30} height={30} src="/images/logos/pcw.png" alt="" />
                          Pointters Community Initiatives
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderNew;
